import clsx from "clsx";
import LystLogo from "img/branding/lyst-logo-2020-black.svg?inline";
import React, { useEffect } from "react";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import { JsonLd } from "react-schemaorg";
import { WebSite } from "schema-dts";
import HeaderBarSearch from "web/react/components/__LEGACY__/app-header/header-bar-search/header-bar-search";
import HeaderGenders from "web/react/components/__LEGACY__/app-header/header-genders/header-genders";
import UserDropdown, {
    UserDropdownProps,
} from "web/react/components/__LEGACY__/app-header/pre-header-bar/user-dropdown";
import AutoCompleteProvider from "web/react/components/__LEGACY__/auto-complete/auto-complete.context";
import { MainNavigationToggleMobile } from "web/react/components/__LEGACY__/main-navigation/main-navigation-toggle-mobile/main-navigation-toggle-mobile";
import AccountLinks from "web/react/components/__LEGACY__/user-links";
import { KevelCampaignControl } from "web/react/components/kevel-campaign-control/kevel-campaign-control";
import { addLystMatte, removeLystMatte } from "web/redux/ducks/layout";
import { ReduxStoreState } from "web/redux/store";
import globals from "web/script/modules/globals";
import { languageIsEnglish } from "web/script/modules/language";
import { AppHeaderBarSerializer } from "web/types/serializers";
import styles from "./header-bar.module.css";

export interface HeaderBarProps extends AppHeaderBarSerializer {
    userDropdown: UserDropdownProps;
}

// This is a workaround for the fact that the JsonLd potentialAction property
// does not include the "query-input" property
type CustomWebSite = WebSite & {
    potentialAction: {
        "@type": "SearchAction";
        target: string;
        "query-input": string;
    };
};

function extractState(state: ReduxStoreState): boolean {
    const { isAssistedSearchOpen, isSearchOpen, isCalloutOpen } = state.appHeaderBarReducer;
    return isAssistedSearchOpen || isSearchOpen || isCalloutOpen;
}

function HeaderBar({
    home_url: homeUrl,
    is_homepage: isHomePage,
    genders,
    is_authenticated: isAuthenticated,
    my_searches_url: mySearchesUrl,
    saved_item_url: savedItemUrl,
    notifications_url: notificationsUrl,
    notifications_count: notificationsCount,
    search_input: searchInput,
    userDropdown,
    absolute_home_url: absoluteHomeUrl,
    absolute_action_url: absoluteActionUrl,
    has_items_in_wishlist: hasItemsInWishlist,
}: HeaderBarProps): React.ReactElement {
    const showMatte = useReduxSelector(extractState);
    const dispatch = useDispatch();

    useEffect(() => {
        if (showMatte) {
            dispatch(addLystMatte());
        } else {
            dispatch(removeLystMatte());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showMatte]);

    function navigateToHomePage(): void {
        globals.window.location.href = homeUrl;
    }

    return (
        <div className={styles.headerBar}>
            <div className={styles.headerBarContainer}>
                <div className={styles.headerBarInner}>
                    <a className={clsx(styles.logo, "hidden-tablet")} href={homeUrl}>
                        <LystLogo />
                    </a>

                    <div className={styles.headerBarLeftContainer}>
                        <div className="hidden-desktop">
                            <MainNavigationToggleMobile />
                        </div>

                        <HeaderGenders genders={genders} />

                        <AutoCompleteProvider>
                            <HeaderBarSearch {...searchInput} />
                            {isHomePage && (
                                <JsonLd<CustomWebSite>
                                    item={{
                                        "@context": "https://schema.org",
                                        "@type": "WebSite",
                                        url: absoluteHomeUrl,
                                        potentialAction: {
                                            "@type": "SearchAction",
                                            target: `${absoluteActionUrl}?q={q}`,
                                            "query-input": "name=q",
                                        },
                                    }}
                                />
                            )}
                        </AutoCompleteProvider>
                    </div>

                    <KevelCampaignControl
                        captureType="header_logo"
                        contextType={"header_logo"}
                        analyticsEventLabel={"header_logo"}
                        defaultAction={navigateToHomePage}
                        continueOptionHref={homeUrl}
                    >
                        <a className={clsx(styles.logo, "hidden-desktop")} href={homeUrl}>
                            <LystLogo />
                        </a>
                    </KevelCampaignControl>

                    {languageIsEnglish() ? (
                        <div className={styles.glyphsContainer}>
                            {isAuthenticated && (
                                <AccountLinks
                                    my_searches_url={mySearchesUrl}
                                    saved_item_url={savedItemUrl}
                                    notifications_url={notificationsUrl}
                                    notifications_count={notificationsCount}
                                    has_items_in_wishlist={hasItemsInWishlist}
                                />
                            )}
                            <UserDropdown
                                className="hidden-desktop"
                                accountDropdown={userDropdown.accountDropdown}
                                accountText={userDropdown.accountText}
                                accountUrl={userDropdown.accountUrl}
                                registerUrl={userDropdown.registerUrl}
                            />
                        </div>
                    ) : (
                        <div
                            className={clsx(styles.glyphsContainer, "hidden-desktop")}
                        /> /* maintain flex layout for non-english mobile header bar */
                    )}
                </div>
            </div>
        </div>
    );
}

export default HeaderBar;
